import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { AI_ASSISTANT_BLUE } from "../constants";

/**
 * Collapsible in-chat draft card: the summary row is always visible, the
 * editor opens on demand (progressive disclosure).
 */
export default function DraftCardShell({
  kicker,
  title,
  summary,
  icon,
  expanded,
  onToggle,
  headerAction,
  children,
  footer,
  tone = "blue",
}) {
  const border = tone === "green" ? "rgba(16,185,129,0.35)" : "rgba(0, 97, 254, 0.25)";
  return (
    <Box
      component="section"
      aria-label={`${kicker}: ${title}`}
      sx={{
        mb: 1.5,
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        bgcolor: "background.card",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ pr: 0.75 }}>
        <ButtonBase
          onClick={onToggle}
          aria-expanded={expanded}
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: "flex-start",
            textAlign: "left",
            px: 1.5,
            py: 1.1,
            gap: 1,
          }}
        >
          {icon ? (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                color: AI_ASSISTANT_BLUE,
                bgcolor: "rgba(0,97,254,0.08)",
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, color: AI_ASSISTANT_BLUE }}>
              {kicker}
            </Typography>
            <Typography noWrap fontWeight={700} sx={{ fontSize: 13.5, color: "ink.strong" }}>
              {title}
            </Typography>
            {summary ? (
              <Typography noWrap sx={{ fontSize: 11.5, color: "#64748b" }}>
                {summary}
              </Typography>
            ) : null}
          </Box>
          <ExpandMoreRoundedIcon
            sx={{
              color: "#64748b",
              transition: "transform .2s ease",
              transform: expanded ? "rotate(180deg)" : "none",
            }}
          />
        </ButtonBase>
        {headerAction}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1.25, borderTop: "1px solid rgba(148,163,184,0.2)", pt: 1.1 }}>{children}</Box>
      </Collapse>
      {footer ? (
        <Box sx={{ px: 1.25, py: 1, borderTop: "1px solid rgba(148,163,184,0.2)", bgcolor: "#f8fbff" }}>
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}

DraftCardShell.propTypes = {
  kicker: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.node,
  icon: PropTypes.node,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  headerAction: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  tone: PropTypes.oneOf(["blue", "green"]),
};

export const primaryButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  color: "#ffffff !important",
  bgcolor: `${AI_ASSISTANT_BLUE} !important`,
  "&:hover": { bgcolor: "#0052d6 !important" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.85) !important", bgcolor: "rgba(0, 97, 254, 0.45) !important" },
};

export const outlineButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  color: `${AI_ASSISTANT_BLUE} !important`,
  borderColor: `${AI_ASSISTANT_BLUE} !important`,
  bgcolor: "#ffffff !important",
};

export const textButtonSx = { textTransform: "none", fontWeight: 700 };
